import { cn } from '@/lib/utils';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';
import { Loader } from '@/components/ui/loader';

const buttonVariants = cva('inline-flex flex-center whitespace-normal  font-semibold transition-all focus-visible:outline-none focus-visible:ring-primary disabled:pointer-events-none disabled:opacity-50 active:opacity-60 active:scale-95 border-2 border-transparent relative  w-auto rounded-xl cursor-pointer', {
	variants: {
		variant: {
			default: 'bg-[var(--primary)] text-[var(--foreground)]',
			destructive: 'bg-[var(--destructive)] text-[var(--destructive-foreground)] shadow-sm hover:bg-[var(--destructive)]/90',
			outline: '!border-4 !border-[var(--foreground)] text-foreground !bg-[var(--primary)]/50 shadow-sm hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
			muted: '!border-2 !border-[var(--foreground)]/50 text-foreground !bg-[var(--primary)]/50 shadow-sm hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
			ghost: 'hover:bg-[var(--accent)] hover:text-[var(--accent-foreground)]',
			link: 'text-[var(--primary)] underline-offset-4 hover:underline',
		},
		size: {
			default: 'h-11 px-4 text-xl',
			xs: '!h-6  px-2 text-[11px] rounded-sm',
			sm: '!h-8  px-3 text-xs rounded-lg',
			lg: '!h-14 px-8 text-xl',
			xl: '!h-16 px-8 py-10 text-2xl ',
			chip: 'px-4 py-3 text-base  ',
			chipSm: 'px-3 py-1.5 text-base ',
			icon: 'h-9 w-9',
		},
	},
	defaultVariants: {
		variant: 'default',
		size: 'default',
	},
});

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
	asChild?: boolean;
	loading?: boolean;
	icon?: React.ReactNode;
	iconPosition?: 'left' | 'right';
	justifySpace?: boolean;
	children?: React.ReactNode;
	className?: any;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, size, asChild = false, icon, iconPosition = 'left', loading = false, justifySpace = false, children, ...props }, ref) => {
	const Comp = asChild ? Slot : 'button';

	// Handle icon-only buttons (when size is 'icon' and no children)
	const isIconOnly = size === 'icon' && !children;

	// Single content element for Slot compatibility
	const buttonContent = (
		<div className='relative w-full h-full flex items-center justify-center'>
			{/* Button content - always rendered but hidden during loading */}
			<div className={` flex w-full items-center ${justifySpace ? 'justify-between' : 'justify-center'} ${loading ? 'opacity-0' : 'opacity-100'}`}>
				{isIconOnly ? (
					icon
				) : icon ? (
					<span className={cn('flex', iconPosition === 'right' ? 'flex-row-reverse' : 'flex-row', justifySpace ? 'w-full' : '')}>
						<span className={cn('flex items-center', justifySpace && iconPosition === 'right' ? 'justify-end pl-2' : 'justify-start pr-2', className?.icon || '')}>{icon}</span>

						<span className={cn('flex items-center', justifySpace ? 'text-left flex-grow' : '', className?.children || '')}>{children}</span>
					</span>
				) : (
					children
				)}
			</div>

			{/* Loading overlay - positioned absolutely on top */}
			{loading && (
				<div className=' absolute flex items-center justify-center w-full h-full pointer-events-none'>
					<Loader visible={true} />
				</div>
			)}
		</div>
	);

	return (
		<Comp
			className={cn(
				buttonVariants({
					variant,
					size,
					className: typeof className === 'string' ? className : className?.button,
				}),
				'relative transition-all duration-500 ease-in-out',
			)}
			ref={ref}
			{...props}
			disabled={loading || props.disabled}>
			{buttonContent}
		</Comp>
	);
});

Button.displayName = 'Button';

export { Button, buttonVariants };
